import React, { useState, useRef, useCallback, useEffect } from 'react';
import type { ApiService } from '@child-nav/shared';
import {
  sendVerificationCode,
  loginWithCode,
  authenticateWithPasskey,
  registerPasskey,
  isPasskeySupported,
  hasStoredPasskey,
  type AuthResult,
} from '../services/WebAuthService';

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 'email' | 'verify' | 'passkey_pending' | 'passkey_offer';

export interface SignInResult {
  email: string;
  token: string;
  refreshToken: string;
  name?: string;
  userId?: string;
  /** Full LoginResponseDto payload — used to sync profiles/connections after sign-in */
  loginResponseData?: Record<string, any>;
}

interface Props {
  apiService: ApiService;
  onSignIn:  (result: SignInResult) => void;
  onSignUp?: () => void;
}

// ─── Colour tokens ────────────────────────────────────────────────────────────
const C = {
  primary:     '#3498db',
  primaryDark: '#2980b9',
  teal:        '#10b981',
  tealDark:    '#059669',
  bg:          '#eef2f7',
  card:        '#ffffff',
  text:        '#1a202c',
  muted:       '#718096',
  border:      '#cbd5e0',
  error:       '#e53e3e',
  errorBg:     '#fff5f5',
  success:     '#38a169',
  successBg:   '#f0fff4',
};

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

// ─── Reusable sub-components ──────────────────────────────────────────────────

const Btn: React.FC<{
  label: string;
  onClick: () => void;
  color?: string;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'solid' | 'outline' | 'ghost';
  icon?: string;
  fullWidth?: boolean;
}> = ({ label, onClick, color = C.primary, disabled, loading, variant = 'solid', icon, fullWidth = true }) => {
  const [hov, setHov] = useState(false);
  const base: React.CSSProperties = {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            8,
    padding:        '12px 20px',
    borderRadius:   8,
    fontSize:       15,
    fontWeight:     600,
    cursor:         disabled || loading ? 'not-allowed' : 'pointer',
    border:         variant === 'outline' ? `2px solid ${color}` : 'none',
    background:     variant === 'solid'   ? (hov && !disabled ? darken(color) : color)
                  : variant === 'outline' ? (hov && !disabled ? `${color}15` : 'transparent')
                  : 'transparent',
    color:          variant === 'solid' ? '#fff' : color,
    opacity:        disabled || loading ? 0.6 : 1,
    width:          fullWidth ? '100%' : 'auto',
    transition:     'background 0.15s, opacity 0.15s',
    outline:        'none',
    fontFamily:     'inherit',
    boxSizing:      'border-box',
  };
  return (
    <button
      style={base}
      onClick={disabled || loading ? undefined : onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      disabled={disabled || loading}
      type="button"
      aria-busy={loading}
    >
      {loading && <Spinner size={16} color={variant === 'solid' ? '#fff' : color} />}
      {!loading && icon && <span>{icon}</span>}
      {label}
    </button>
  );
};

const Spinner: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = C.primary }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" fill="none" strokeDasharray="31.4" strokeDashoffset="10" />
  </svg>
);

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    background:   C.errorBg,
    border:       `1px solid ${C.error}`,
    borderRadius: 8,
    padding:      '10px 14px',
    color:        C.error,
    fontSize:     14,
    marginBottom: 16,
    display:      'flex',
    alignItems:   'flex-start',
    gap:          8,
  }}>
    <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
    <span>{message}</span>
  </div>
);

const SuccessBanner: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    background:   C.successBg,
    border:       `1px solid ${C.success}`,
    borderRadius: 8,
    padding:      '10px 14px',
    color:        C.success,
    fontSize:     14,
    marginBottom: 16,
    display:      'flex',
    alignItems:   'flex-start',
    gap:          8,
  }}>
    <span style={{ fontSize: 16, flexShrink: 0 }}>✅</span>
    <span>{message}</span>
  </div>
);

// ─── Digit code input ─────────────────────────────────────────────────────────

const CODE_LENGTH = 6;

const CodeInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const digits = value.padEnd(CODE_LENGTH, ' ').split('').slice(0, CODE_LENGTH);

  const handleKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = digits.map((d, i) => (i === idx ? ' ' : d)).join('').trimEnd();
      onChange(next + (next.length < idx ? '' : ''));
      // Build updated string
      const arr = [...digits];
      if (arr[idx].trim() === '' && idx > 0) {
        arr[idx - 1] = ' ';
        onChange(arr.join('').replace(/ +$/, ''));
        refs.current[idx - 1]?.focus();
      } else {
        arr[idx] = ' ';
        onChange(arr.join('').replace(/ +$/, ''));
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < CODE_LENGTH - 1) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handleChange = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    if (!char) return;
    const arr = [...digits];
    arr[idx] = char;
    onChange(arr.join('').replace(/ +$/, ''));
    if (idx < CODE_LENGTH - 1) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 8 }}>
      {Array.from({ length: CODE_LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digits[i].trim()}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          style={{
            width:       48,
            height:      56,
            fontSize:    22,
            fontWeight:  700,
            textAlign:   'center',
            border:      `2px solid ${digits[i].trim() ? C.primary : C.border}`,
            borderRadius: 8,
            outline:     'none',
            background:  '#fff',
            color:       C.text,
            caretColor:  'transparent',
            transition:  'border-color 0.15s',
            fontFamily:  'monospace',
          }}
        />
      ))}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const WebSignInView: React.FC<Props> = ({ apiService, onSignIn, onSignUp }) => {
  const [step,              setStep]             = useState<Step>('email');
  const [email,             setEmail]            = useState('');
  const [userName,          setUserName]         = useState(
    () => {
      try { return window.localStorage.getItem('user_name') ?? ''; } catch { return ''; }
    },
  );
  const [code,              setCode]             = useState('');
  const [error,             setError]            = useState('');
  const [info,              setInfo]             = useState('');
  const [loading,           setLoading]          = useState(false);
  const [resendCool,        setResendCool]       = useState(0);  // countdown seconds
  const [verificationToken, setVerificationToken] = useState('');
  const [authData,          setAuthData]         = useState<AuthResult['data'] | null>(null);

  const passkeyAvail = isPasskeySupported();
  const hasPasskey   = hasStoredPasskey();

  // Resend cooldown timer
  useEffect(() => {
    if (resendCool <= 0) return;
    const t = setTimeout(() => setResendCool((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCool]);

  const clearMessages = () => { setError(''); setInfo(''); };

  // ── Step 1: send code ──────────────────────────────────────────────────────

  const handleSendCode = useCallback(async () => {
    clearMessages();
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!userName.trim()) {
      setError('Please enter your name on this account.');
      return;
    }
    setLoading(true);
    const result = await sendVerificationCode(
      email.trim().toLowerCase(),
      userName.trim(),
      apiService,
    );
    setLoading(false);

    if (result.success && result.verificationToken) {
      setVerificationToken(result.verificationToken);
      setStep('verify');
      setInfo(`A verification code has been sent to ${email}.`);
      setResendCool(60);
    } else {
      setError(result.message ?? 'Could not send the code. Please try again.');
    }
  }, [email, userName, apiService]);

  // ── Step 1: passkey ────────────────────────────────────────────────────────

  const handlePasskeySignIn = useCallback(async () => {
    clearMessages();
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address first.');
      return;
    }
    setLoading(true);
    setStep('passkey_pending');

    // userName is a local label only; fall back to email so persistSession always
    // has something to store — the backend does not need it for WebAuthn.
    const result = await authenticateWithPasskey(
      email.trim().toLowerCase(),
      userName.trim() || email.trim(),
      apiService,
    );
    setLoading(false);

    if (result.success && result.data) {
      setAuthData(result.data);
      onSignIn(result.data as SignInResult);
    } else {
      setStep('email');
      setError(result.message ?? 'Passkey sign-in failed.');
    }
  }, [email, userName, apiService, onSignIn]);

  // ── Step 2: verify code ────────────────────────────────────────────────────

  const handleVerify = useCallback(async () => {
    clearMessages();
    if (code.length < CODE_LENGTH) {
      setError('Please enter all 6 digits of your verification code.');
      return;
    }
    if (!verificationToken) {
      setError('Session expired. Please go back and request a new code.');
      return;
    }
    setLoading(true);
    const result = await loginWithCode(
      email.trim().toLowerCase(),
      userName.trim(),
      code,
      verificationToken,
      apiService,
    );
    setLoading(false);

    if (result.success && result.data) {
      setAuthData(result.data);
      // If passkeys are supported and none is saved yet, offer to create one
      if (passkeyAvail && !hasPasskey) {
        setStep('passkey_offer');
      } else {
        onSignIn(result.data as SignInResult);
      }
    } else {
      setError(result.message ?? 'Verification failed. Please try again.');
      setCode('');
    }
  }, [code, email, apiService, passkeyAvail, hasPasskey, onSignIn]);

  const handleResend = useCallback(async () => {
    if (resendCool > 0) return;
    clearMessages();
    setVerificationToken('');
    setLoading(true);
    const result = await sendVerificationCode(
      email.trim().toLowerCase(),
      userName.trim(),
      apiService,
    );
    setLoading(false);
    if (result.success && result.verificationToken) {
      setVerificationToken(result.verificationToken);
      setInfo('A new code has been sent.');
      setCode('');
      setResendCool(60);
    } else {
      setError(result.message ?? 'Could not resend the code.');
    }
  }, [email, userName, apiService, resendCool]);

  // ── Step 3: passkey offer ──────────────────────────────────────────────────

  const handleSavePasskey = useCallback(async () => {
    if (!authData) return;
    setLoading(true);
    await registerPasskey(
      authData.email,
      authData.userId ?? authData.email,
      authData.userName ?? userName.trim(),
      apiService,
    );
    setLoading(false);
    onSignIn(authData as SignInResult);
  }, [authData, userName, apiService, onSignIn]);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .sign-in-card { animation: fadeIn 0.25s ease; }
        * { box-sizing: border-box; }
      `}</style>

      <div style={{
        minHeight:       '100vh',
        background:      C.bg,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         20,
        fontFamily:      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        <div
          className="sign-in-card"
          style={{
            background:   C.card,
            borderRadius: 16,
            boxShadow:    '0 4px 24px rgba(0,0,0,0.10)',
            width:        '100%',
            maxWidth:     420,
            padding:      '40px 36px 32px',
          }}
        >
          {/* Logo + heading */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{
              width:        64,
              height:       64,
              borderRadius: '50%',
              background:   `linear-gradient(135deg, ${C.primary} 0%, ${C.teal} 100%)`,
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'center',
              margin:       '0 auto 16px',
              fontSize:     28,
            }}>
              🧒
            </div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>
              Child Nav
            </h1>
            <p style={{ margin: '6px 0 0', color: C.muted, fontSize: 14 }}>
              {step === 'email'           && 'Sign in to your account'}
              {step === 'verify'          && 'Check your email'}
              {step === 'passkey_pending' && 'Authenticating…'}
              {step === 'passkey_offer'   && 'Signed in successfully'}
            </p>
          </div>

          {/* Error / info banners */}
          {error && <ErrorBanner   message={error} />}
          {info  && <SuccessBanner message={info}  />}

          {/* ── Step: email ──────────────────────────────────────────────── */}
          {step === 'email' && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.muted }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearMessages(); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                  placeholder="you@example.com"
                  autoFocus
                  autoComplete="email"
                  disabled={loading}
                  style={{
                    width:        '100%',
                    padding:      '11px 14px',
                    border:       `2px solid ${C.border}`,
                    borderRadius: 8,
                    fontSize:     15,
                    color:        C.text,
                    outline:      'none',
                    transition:   'border-color 0.15s',
                    fontFamily:   'inherit',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = C.primary; }}
                  onBlur={(e)  => { e.currentTarget.style.borderColor = C.border;  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.muted }}>
                  Your name on this account
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => { setUserName(e.target.value); clearMessages(); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                  placeholder="e.g. Mom, Dad's iPad, Nurse Sarah"
                  autoComplete="name"
                  disabled={loading}
                  style={{
                    width:        '100%',
                    padding:      '11px 14px',
                    border:       `2px solid ${C.border}`,
                    borderRadius: 8,
                    fontSize:     15,
                    color:        C.text,
                    outline:      'none',
                    transition:   'border-color 0.15s',
                    fontFamily:   'inherit',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = C.primary; }}
                  onBlur={(e)  => { e.currentTarget.style.borderColor = C.border;  }}
                />
                <p style={{ margin: '4px 0 0', fontSize: 12, color: C.muted }}>
                  Each person on the account has a unique name. This identifies who is signing in.
                </p>
              </div>

              <Btn
                label="Send Verification Code"
                onClick={handleSendCode}
                color={C.primary}
                loading={loading}
                disabled={!email.trim() || !userName.trim()}
                icon="✉️"
              />

              {passkeyAvail && (
                <>
                  <Divider label="or" />
                  <Btn
                    label={hasPasskey ? 'Sign in with Passkey' : 'Use Passkey'}
                    onClick={handlePasskeySignIn}
                    color={C.teal}
                    variant="outline"
                    loading={false}
                    disabled={loading}
                    icon="🔑"
                  />
                  {!hasPasskey && (
                    <p style={{ textAlign: 'center', fontSize: 12, color: C.muted, marginTop: 8 }}>
                      No passkey saved yet? — sign in once with a code to set one up.
                    </p>
                  )}
                </>
              )}

              {onSignUp && (
                <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: C.muted }}>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={onSignUp}
                    disabled={loading}
                    style={{
                      background:  'none',
                      border:      'none',
                      color:       C.primary,
                      fontSize:    13,
                      cursor:      loading ? 'default' : 'pointer',
                      padding:     0,
                      fontFamily:  'inherit',
                      opacity:     loading ? 0.6 : 1,
                    }}
                  >
                    Sign up
                  </button>
                </p>
              )}
            </>
          )}

          {/* ── Step: verify ─────────────────────────────────────────────── */}
          {step === 'verify' && (
            <>
              <p style={{ textAlign: 'center', fontSize: 14, color: C.muted, marginBottom: 20 }}>
                Enter the 6-digit code we sent to<br />
                <strong style={{ color: C.text }}>{email}</strong>
              </p>

              <CodeInput value={code} onChange={setCode} disabled={loading} />

              <p style={{
                textAlign:    'center',
                fontSize:     12,
                color:        C.muted,
                marginBottom: 20,
                marginTop:    4,
              }}>
                The code expires in 10 minutes.
              </p>

              <Btn
                label="Verify Code"
                onClick={handleVerify}
                color={C.primary}
                loading={loading}
                disabled={code.length < CODE_LENGTH}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <LinkBtn
                  label="← Change email"
                  onClick={() => { setStep('email'); setCode(''); setVerificationToken(''); clearMessages(); }}
                  disabled={loading}
                />
                <LinkBtn
                  label={resendCool > 0 ? `Resend code (${resendCool}s)` : 'Resend code'}
                  onClick={handleResend}
                  disabled={loading || resendCool > 0}
                />
              </div>
            </>
          )}

          {/* ── Step: passkey pending ─────────────────────────────────────── */}
          {step === 'passkey_pending' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Spinner size={40} color={C.teal} />
              <p style={{ marginTop: 16, color: C.muted, fontSize: 14 }}>
                Follow your browser's prompts to authenticate with your passkey.
              </p>
              <LinkBtn
                label="Cancel"
                onClick={() => { setStep('email'); clearMessages(); }}
                disabled={false}
              />
            </div>
          )}

          {/* ── Step: passkey offer ───────────────────────────────────────── */}
          {step === 'passkey_offer' && (
            <>
              <p style={{ textAlign: 'center', fontSize: 15, color: C.text, marginBottom: 20 }}>
                Would you like to save a <strong>passkey</strong> so you can sign in faster next time — no code needed?
              </p>
              <Btn
                label="Save Passkey"
                onClick={handleSavePasskey}
                color={C.teal}
                loading={loading}
                icon="🔑"
              />
              <div style={{ marginTop: 10 }}>
                <Btn
                  label="Skip for now"
                  onClick={() => authData && onSignIn(authData as SignInResult)}
                  color={C.muted}
                  variant="ghost"
                  loading={false}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Tiny helpers used only here ──────────────────────────────────────────────

const Divider: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0' }}>
    <div style={{ flex: 1, height: 1, background: C.border }} />
    <span style={{ padding: '0 12px', color: C.muted, fontSize: 13 }}>{label}</span>
    <div style={{ flex: 1, height: 1, background: C.border }} />
  </div>
);

const LinkBtn: React.FC<{ label: string; onClick: () => void; disabled?: boolean }> = ({ label, onClick, disabled }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    style={{
      background:    'none',
      border:        'none',
      color:         disabled ? C.muted : C.primary,
      fontSize:      13,
      cursor:        disabled ? 'default' : 'pointer',
      padding:       '4px 0',
      textDecoration: 'underline',
      fontFamily:    'inherit',
      opacity:       disabled ? 0.6 : 1,
    }}
  >
    {label}
  </button>
);

// Darken a hex colour by ~10 %
function darken(hex: string): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (n >> 16) - 25);
  const g = Math.max(0, ((n >> 8) & 0xff) - 25);
  const b = Math.max(0, (n & 0xff) - 25);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}
